import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

const MENU_DRAWER_PORTAL_ID = 'menu-drawer-body-portal';
const HEADER_DRAWER_ID = 'HeaderDrawer-Menu';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {HTMLElement | null} */
  #portalRoot = null;

  /** @type {HTMLElement | null} */
  #drawerEl = null;

  /** @type {HTMLElement | null} */
  #backdropEl = null;

  /** @type {boolean} */
  #isPortaled = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.id) this.id = HEADER_DRAWER_ID;

    this.#setupAnimatedElementListeners();
    this.#setupFeaturedStrip();
    this.#setupPanelCarousels();

    document.addEventListener('localization-drawer:opened', this.#onLocalizationOpened);
    document.addEventListener('localization-drawer:closed', this.#onLocalizationClosed);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // Language / country drawer sits above the menu — let it handle Escape first
    if (document.querySelector('.header-localization.is-open')) return;

    const openPanels = this.#getOpenPanels();
    if (openPanels.length > 0) {
      this.#closePanel(openPanels[openPanels.length - 1]);
      return;
    }

    if (this.isOpen) {
      this.close();
    }
  };

  /** Pause menu focus trap so the language drawer can receive focus/clicks */
  #onLocalizationOpened = () => {
    if (this.isOpen) removeTrapFocus();
  };

  /** Restore menu focus trap after language drawer closes (menu still open) */
  #onLocalizationClosed = () => {
    if (!this.isOpen) return;
    if (document.querySelector('.header-localization.is-open')) return;

    requestAnimationFrame(() => {
      if (!this.isOpen || document.querySelector('.header-localization.is-open')) return;
      const openPanels = this.#getOpenPanels();
      if (openPanels.length > 0) {
        trapFocus(openPanels[openPanels.length - 1]);
      } else {
        trapFocus(this.#menuDrawer ?? this.refs.details);
      }
    });
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Drawer may live in a body portal while open — always resolve from stored ref first.
   * @returns {HTMLElement | null}
   */
  get #menuDrawer() {
    if (this.#drawerEl?.isConnected) return this.#drawerEl;
    return this.querySelector('.menu-drawer');
  }

  get #mainMenu() {
    return this.#menuDrawer?.querySelector('.menu-drawer__main-menu') ?? null;
  }

  get #panelStack() {
    return this.#menuDrawer?.querySelector('.menu-drawer__panel-stack') ?? null;
  }

  /**
   * Search in drawer (including when portaled to body).
   * @param {string} selector
   * @returns {Element | null}
   */
  #drawerQuery(selector) {
    return this.#menuDrawer?.querySelector(selector) ?? this.querySelector(selector);
  }

  /**
   * @param {string} selector
   * @returns {NodeListOf<Element> | Element[]}
   */
  #drawerQueryAll(selector) {
    const root = this.#menuDrawer;
    if (root) return root.querySelectorAll(selector);
    return this.querySelectorAll(selector);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getOpenPanels() {
    return Array.from(this.#drawerQueryAll('[data-submenu-panel].is-open'));
  }

  /**
   * @param {string} panelId
   * @returns {HTMLElement | null}
   */
  #getPanelById(panelId) {
    return /** @type {HTMLElement | null} */ (this.#drawerQuery(`[data-submenu-panel="${panelId}"]`));
  }

  /**
   * @param {Event | undefined} event
   * @returns {string | null}
   */
  #getPanelIdFromEvent(event) {
    const raw =
      event?.target instanceof Element
        ? event.target
        : event?.composedPath?.()?.find((n) => n instanceof Element) ?? null;
    if (!(raw instanceof Element)) return null;

    const trigger = raw.closest('[data-panel-target]');
    if (trigger instanceof HTMLElement) {
      return trigger.dataset.panelTarget ?? null;
    }

    const panel = raw.closest('[data-submenu-panel]');
    if (panel instanceof HTMLElement) {
      return panel.dataset.submenuPanel ?? null;
    }

    return null;
  }

  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open main drawer (hamburger) or legacy panel handler.
   * Desktop submenu drawers have no <summary> — still must open.
   * Portal / body lock is mobile-only.
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();

    if (event?.target instanceof Element && event.target.closest('[data-panel-target]')) {
      this.openPanel(event);
      return;
    }

    const details = this.refs.details;
    if (!details) return;

    const summary = details.querySelector('summary');
    const usePortal = this.#shouldUsePortal();

    details.setAttribute('open', '');
    details.classList.add('menu-open');
    summary?.setAttribute('aria-expanded', 'true');

    if (usePortal) {
      document.body.classList.add('menu-drawer-open');
      document.documentElement.setAttribute('scroll-lock', '');
      document.addEventListener('keyup', this.#onKeyUp);
      this.#portalToBody();
    } else {
      this.addEventListener('keyup', this.#onKeyUp);
    }

    this.#resetFeaturedStripScroll();

    // Desktop submenu: no focus trap — it fights accordion/close clicks under transparent headers.
    if (!this.classList.contains('header__drawer--submenu')) {
      requestAnimationFrame(() => {
        trapFocus(this.#menuDrawer ?? details);
      });
    }
  }

  /**
   * Portal the hamburger drawer on every viewport.
   * Store uses desktop_navigation_type "hamburger" — desktop opens this same
   * drawer (horizontal submenu drawers are display:none). Without portal at
   * ≥1025px the panel sits under the homepage hero.
   * Desktop submenu drawers (header__drawer--submenu) never match this.
   * @returns {boolean}
   */
  #shouldUsePortal() {
    return this.classList.contains('header__drawer--mobile');
  }

  /**
   * Window capture runs BEFORE document's on:click handler.
   * Handles openPanel / back / close while the drawer is portaled to body
   * (closest('header-drawer') fails outside the component tree).
   * @param {Event} event
   */
  #onPortaledClick = (event) => {
    if (!this.#isPortaled || !this.#portalRoot) return;
    if (!(event.target instanceof Element)) return;
    if (!this.#portalRoot.contains(event.target)) return;

    const panelTrigger = event.target.closest('[data-panel-target]');
    if (panelTrigger instanceof HTMLElement && this.#portalRoot.contains(panelTrigger)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openPanel(event);
      return;
    }

    const backBtn = event.target.closest('.menu-drawer__back-pill');
    if (backBtn instanceof HTMLElement && this.#portalRoot.contains(backBtn)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.back(event);
      return;
    }

    const closeBtn = event.target.closest('.menu-drawer__top-bar-close');
    const onBackdrop = event.target.classList.contains('menu-drawer__backdrop');
    if (onBackdrop || (closeBtn instanceof HTMLElement && this.#portalRoot.contains(closeBtn))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    }
  };

  /**
   * Move drawer + backdrop to document.body so they paint above every stacking context.
   * Wraps content in `.header__drawer--mobile` so submenu/panel CSS still matches.
   */
  #portalToBody() {
    if (this.#isPortaled || !this.#shouldUsePortal()) return;

    const details = this.refs.details;
    const drawer = details.querySelector('.menu-drawer');
    const backdrop = details.querySelector('.menu-drawer__backdrop');
    if (!(drawer instanceof HTMLElement)) return;

    if (!this.id) this.id = HEADER_DRAWER_ID;

    let portal = document.getElementById(MENU_DRAWER_PORTAL_ID);
    if (!(portal instanceof HTMLElement)) {
      portal = document.createElement('div');
      portal.id = MENU_DRAWER_PORTAL_ID;
      portal.setAttribute('data-menu-drawer-portal', '');
      document.body.appendChild(portal);
    }

    const host = document.createElement('div');
    host.className = `header-drawer header__drawer--mobile menu-drawer-portal-host ${this.className}`.trim();

    this.#drawerEl = drawer;
    this.#backdropEl = backdrop instanceof HTMLElement ? backdrop : null;
    this.#portalRoot = portal;

    if (this.#backdropEl) host.appendChild(this.#backdropEl);
    host.appendChild(drawer);

    portal.replaceChildren(host);
    portal.classList.add('is-open');
    this.#isPortaled = true;

    // Window capture → before document on:click; stopImmediatePropagation keeps it reliable
    window.addEventListener('click', this.#onPortaledClick, true);
  }

  /**
   * Restore drawer + backdrop back under the details trigger.
   */
  #unportalFromBody() {
    if (!this.#isPortaled) return;

    const details = this.refs.details;
    const drawer = this.#drawerEl;
    const backdrop = this.#backdropEl;

    window.removeEventListener('click', this.#onPortaledClick, true);

    if (drawer) details.appendChild(drawer);
    if (backdrop) details.appendChild(backdrop);

    this.#portalRoot?.classList.remove('is-open');
    this.#portalRoot?.replaceChildren();

    this.#drawerEl = null;
    this.#backdropEl = null;
    this.#portalRoot = null;
    this.#isPortaled = false;
  }

  /**
   * Open a submenu drawer for a main menu (or nested) category
   * @param {Event} event
   */
  openPanel(event) {
    event?.preventDefault();

    const panelId = this.#getPanelIdFromEvent(event);
    if (!panelId) return;

    const panel = this.#getPanelById(panelId);
    if (!panel) return;

    const raw =
      event?.target instanceof Element
        ? event.target
        : event?.composedPath?.()?.find((n) => n instanceof Element) ?? null;
    const trigger = raw instanceof Element ? raw.closest('[data-panel-target]') : null;
    const panelLevel = panel.dataset.panelLevel;

    if (panelLevel === 'root') {
      this.#getOpenPanels().forEach((openPanel) => this.#closePanel(openPanel, false));
    } else {
      panel.parentElement?.querySelectorAll('[data-submenu-panel].is-open').forEach((openPanel) => {
        if (openPanel !== panel) this.#closePanel(/** @type {HTMLElement} */ (openPanel), false);
      });
    }

    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.#menuDrawer?.classList.add('menu-drawer--submenu-open');
    if (panelLevel === 'root') {
      this.#mainMenu?.setAttribute('aria-hidden', 'true');
      this.#panelStack?.setAttribute('aria-hidden', 'false');
    }

    requestAnimationFrame(() => {
      trapFocus(panel);
      this.#refreshPanelCarousels(panel);
    });
  }

  back(event) {
    event?.preventDefault();

    const panelId = this.#getPanelIdFromEvent(event);
    const panel = panelId ? this.#getPanelById(panelId) : this.#getOpenPanels().at(-1);

    if (panel) {
      this.#closePanel(panel);
      return;
    }

    this.close();
  }

  close() {
    this.#getOpenPanels().forEach((panel) => this.#closePanel(panel, false));
    this.#close(this.refs.details);

    // Desktop submenu: sync container visibility + restore if body-mounted.
    const submenuContainer = this.closest('.submenu-drawer-container');
    if (submenuContainer instanceof HTMLElement) {
      submenuContainer.classList.remove('submenu-drawer-container--visible');
      submenuContainer.classList.add('submenu-drawer-container--hidden');
      document.body.classList.remove('submenu-drawer-open');
      document.querySelectorAll('.header__drawer-menu-item[aria-expanded="true"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (submenuContainer._pzHome?.parent instanceof Node) {
        const { parent, next } = submenuContainer._pzHome;
        if (next instanceof Node && next.parentNode === parent) parent.insertBefore(submenuContainer, next);
        else parent.appendChild(submenuContainer);
        submenuContainer._pzHome = null;
      }
      document.dispatchEvent(
        new CustomEvent('submenu-drawer:closed', { detail: { container: submenuContainer } })
      );
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} [restoreFocus=true]
   */
  #closePanel(panel, restoreFocus = true) {
    const panelId = panel.dataset.submenuPanel;
    const trigger = panelId
      ? /** @type {HTMLElement | null} */ (this.#drawerQuery(`[data-panel-target="${panelId}"]`))
      : null;

    panel.querySelectorAll('[data-submenu-panel].is-open').forEach((nested) => {
      this.#closePanel(/** @type {HTMLElement} */ (nested), false);
    });

    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');

    const remaining = this.#getOpenPanels();

    if (remaining.length === 0) {
      this.#menuDrawer?.classList.remove('menu-drawer--submenu-open');
      this.#mainMenu?.setAttribute('aria-hidden', 'false');
      this.#panelStack?.setAttribute('aria-hidden', 'true');
      this.#resetFeaturedStripScroll();
    }

    onAnimationEnd(panel, () => {
      if (!restoreFocus) return;

      const openPanels = this.#getOpenPanels();
      if (openPanels.length > 0) {
        trapFocus(openPanels[openPanels.length - 1]);
      } else {
        trapFocus(this.#menuDrawer ?? this.refs.details);
      }
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const wasPortaled = this.#isPortaled;

    document.body.classList.remove('menu-drawer-open');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#portalRoot?.classList.remove('is-open');

    let finished = false;

    const finishClose = () => {
      if (finished) return;
      finished = true;
      this.#unportalFromBody();
      reset(details);
      this.#getOpenPanels().forEach((panel) => this.#closePanel(panel, false));
      this.#menuDrawer?.classList.remove('menu-drawer--submenu-open');
      this.#mainMenu?.setAttribute('aria-hidden', 'false');
      this.#panelStack?.setAttribute('aria-hidden', 'true');

      if (details === this.refs.details) {
        removeTrapFocus();
      }
    };

    if (wasPortaled || this.classList.contains('header__drawer--submenu')) {
      // Close immediately — delayed finish left desktop submenu stuck open/behind page.
      finishClose();
    } else {
      onAnimationEnd(details, finishClose);
      setTimeout(finishClose, 500);
    }
  }

  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #setupFeaturedStrip() {
    const featured = this.querySelector('[data-featured-strip]');
    if (!featured) return;

    const scroller = featured.querySelector('[data-featured-strip-scroller]');
    const prevBtn = featured.querySelector('[data-featured-strip-prev]');
    const nextBtn = featured.querySelector('[data-featured-strip-next]');
    const thumb = featured.querySelector('[data-featured-strip-thumb]');

    if (!(scroller instanceof HTMLElement)) return;

    /** @type {ResizeObserver | undefined} */
    let resizeObserver;

    const getScrollStep = () => {
      const firstItem = scroller.querySelector('.menu-drawer__featured-content-list-item');
      if (!(firstItem instanceof HTMLElement)) return scroller.clientWidth * 0.85;
      const gap = Number.parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap) || 12;
      return firstItem.getBoundingClientRect().width + gap;
    };

    const updateFeaturedStrip = () => {
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const canScroll = maxScroll > 2;

      featured.classList.toggle('menu-drawer__featured-content--scrollable', canScroll);

      if (prevBtn instanceof HTMLButtonElement) {
        prevBtn.disabled = !canScroll || scroller.scrollLeft <= 2;
      }
      if (nextBtn instanceof HTMLButtonElement) {
        nextBtn.disabled = !canScroll || scroller.scrollLeft >= maxScroll - 2;
      }

      if (thumb instanceof HTMLElement) {
        if (!canScroll) {
          thumb.style.width = '100%';
          thumb.style.left = '0';
        } else {
          const thumbRatio = scroller.clientWidth / scroller.scrollWidth;
          const thumbWidth = Math.max(thumbRatio * 100, 12);
          const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
          const thumbOffset = scrollRatio * (100 - thumbWidth);
          thumb.style.width = `${thumbWidth}%`;
          thumb.style.left = `${Math.max(0, thumbOffset)}%`;
        }
      }
    };

    const onPrev = () => {
      scroller.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    };
    const onNext = () => {
      scroller.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    };

    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);
    scroller.addEventListener('scroll', updateFeaturedStrip, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateFeaturedStrip);
      resizeObserver.observe(scroller);
    }

    updateFeaturedStrip();

    this.#featuredStripCleanup = () => {
      prevBtn?.removeEventListener('click', onPrev);
      nextBtn?.removeEventListener('click', onNext);
      scroller.removeEventListener('scroll', updateFeaturedStrip);
      resizeObserver?.disconnect();
    };
  }

  #resetFeaturedStripScroll() {
    const scroller = this.#drawerQuery('[data-featured-strip-scroller]');
    if (scroller instanceof HTMLElement) {
      scroller.scrollLeft = 0;
    }
  }

  /** @type {(() => void) | undefined} */
  #featuredStripCleanup;

  #setupPanelCarousels() {
    this.querySelectorAll('[data-panel-collection-carousel]').forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;
      this.#initPanelCarousel(carousel);
    });
  }

  /**
   * @param {HTMLElement} carousel
   */
  #initPanelCarousel(carousel) {
    if (carousel._panelCarouselCleanup) return;

    const scroller = carousel.querySelector('[data-panel-carousel-scroller]');
    const prevBtn = carousel.querySelector('[data-panel-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-panel-carousel-next]');
    const thumb = carousel.querySelector('[data-panel-carousel-thumb]');

    if (!(scroller instanceof HTMLElement)) return;

    /** @type {ResizeObserver | undefined} */
    let resizeObserver;

    const getScrollStep = () => {
      const firstItem = scroller.querySelector('.menu-drawer__panel-carousel-item');
      if (!(firstItem instanceof HTMLElement)) return scroller.clientWidth * 0.85;
      const gap = Number.parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap) || 12;
      return firstItem.getBoundingClientRect().width + gap;
    };

    const updateCarousel = () => {
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const canScroll = maxScroll > 2;

      carousel.classList.toggle('menu-drawer__panel-carousel--scrollable', canScroll);

      if (prevBtn instanceof HTMLButtonElement) {
        prevBtn.disabled = !canScroll || scroller.scrollLeft <= 2;
      }
      if (nextBtn instanceof HTMLButtonElement) {
        nextBtn.disabled = !canScroll || scroller.scrollLeft >= maxScroll - 2;
      }

      if (thumb instanceof HTMLElement) {
        if (!canScroll) {
          thumb.style.width = '100%';
          thumb.style.left = '0';
        } else {
          const thumbRatio = scroller.clientWidth / scroller.scrollWidth;
          const thumbWidth = Math.max(thumbRatio * 100, 12);
          const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
          const thumbOffset = scrollRatio * (100 - thumbWidth);
          thumb.style.width = `${thumbWidth}%`;
          thumb.style.left = `${Math.max(0, thumbOffset)}%`;
        }
      }
    };

    const onPrev = () => {
      scroller.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    };
    const onNext = () => {
      scroller.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    };

    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);
    scroller.addEventListener('scroll', updateCarousel, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateCarousel);
      resizeObserver.observe(scroller);
    }

    updateCarousel();

    carousel._panelCarouselCleanup = () => {
      prevBtn?.removeEventListener('click', onPrev);
      nextBtn?.removeEventListener('click', onNext);
      scroller.removeEventListener('scroll', updateCarousel);
      resizeObserver?.disconnect();
      delete carousel._panelCarouselCleanup;
    };
  }

  /**
   * @param {ParentNode | null | undefined} scope
   */
  #refreshPanelCarousels(scope) {
    const root = scope instanceof HTMLElement ? scope : this.#menuDrawer ?? this;
    root.querySelectorAll('[data-panel-collection-carousel]').forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;
      carousel._panelCarouselCleanup?.();
      this.#initPanelCarousel(carousel);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('click', this.#onPortaledClick, true);
    document.removeEventListener('localization-drawer:opened', this.#onLocalizationOpened);
    document.removeEventListener('localization-drawer:closed', this.#onLocalizationClosed);
    this.#featuredStripCleanup?.();
    this.#featuredStripCleanup = undefined;
    document.body.classList.remove('menu-drawer-open');
    document.documentElement.removeAttribute('scroll-lock');
    this.#unportalFromBody();
    this.querySelectorAll('[data-panel-collection-carousel]').forEach((carousel) => {
      if (carousel instanceof HTMLElement) carousel._panelCarouselCleanup?.();
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('menu-drawer-open');
}
